import {computed, ref, type Ref} from 'vue';
import type {QuasarOption} from '@/ts/types/component/select-multichoice';

function useSelectInputImpl<T>(
  allOptions: Ref<QuasarOption<T>[]>,
  modelValue: Ref<T | null>,
): any {
  const options = ref([] as QuasarOption<T>[]);
  const inputValue = ref<string>('');

  options.value = allOptions.value;

  const value = computed<QuasarOption<T> | null>({
    get() {
      if (!modelValue.value) {
        return null;
      }
      return allOptions.value.find(option => option.value === modelValue.value) ?? null;
    },
    set(newValue) {
      modelValue.value = newValue?.value ?? null;
      
      if (newValue) {
        inputValue.value = newValue.label;
      }
    },
  });

  function filterFn(val: string, update: (arg: () => void) => void): void {
    inputValue.value = val;

    if (val !== '' && val !== value.value?.label) {
      modelValue.value = null;
    }

    if (val === '') {
      update(() => {
        options.value = allOptions.value;
      });
    } else {
      update(() => {
        options.value = allOptions.value.filter(item =>
          item.label.toLowerCase().includes(val?.toLowerCase() ?? ''));
      });
    }
  }

  function onInputValue(val: string): void {
    inputValue.value = val;
  }

  return {
    options,
    inputValue,
    value,
    filterFn,
    onInputValue,
  };
}

/** Provides filterable select input state management with two-way model binding and option search */
export function useSelectInput<T>(
  allOptions: Ref<QuasarOption<T>[]>,
  modelValue: Ref<T | null>,
): any {
  return useSelectInputImpl(allOptions, modelValue);
}